import styles from './DoctorPatientDetailsPage.module.css';

import classNames from 'classnames';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';

import BackIcon from '../assets/icons/back.svg?react';
import TrashIcon from '../assets/icons/trash.svg?react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, generatePath, useNavigate, useParams } from 'react-router-dom';

import { queryClient } from '../App';
import { CommonApi, OrderApi, PeopleApi } from '../api';
import PdfIcon from '../assets/icons/pdf.png';
import { DoctorOrderDetails, PrivateRoute } from '../components';
import { RoutePath } from '../config';
import { DoctorInnerPageLayout } from '../layouts';
import { formatMoney, getOrderStatusConfig } from '../utils';

export const DoctorPatientDetailsPage = () => {
  const [tab, setTab] = useState<'history' | 'active' | 'documents'>('history');
  const { patientId } = useParams<{ patientId?: string }>();
  const [, setIsUploading] = useState<boolean>(false);
  const [, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const navigate = useNavigate();
  const { data: patient } = useQuery({
    enabled: !!patientId,
    queryFn: () =>
      patientId ? PeopleApi.getPatientDetails(patientId) : Promise.reject(),
    queryKey: [PeopleApi.KEY, 'getPatientDetails', patientId],
  });

  const { data: orderHistory = [] } = useQuery({
    enabled: !!patientId,
    queryFn: () =>
      patientId ? OrderApi.getHistoryByPatientId(patientId) : Promise.reject(),
    queryKey: [OrderApi.KEY, 'getHistoryByPatientId', patientId],
  });

  const sortedOrderHistory = useMemo(
    () =>
      [...orderHistory].sort((a, b) => {
        const tA = new Date(a.status_date || 0).getTime();
        const tB = new Date(b.status_date || 0).getTime();
        return tB - tA;
      }),
    [orderHistory],
  );

  const { mutate: deleteDocument } = useMutation({
    mutationFn: (documentId: number) => CommonApi.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries([
        PeopleApi.KEY,
        'getPatientDetails',
        patientId,
      ]);
    },
  });

  const { mutate: uploadDocument } = useMutation({
    mutationFn: (formData: FormData) => CommonApi.uploadDocument(formData),
    onSuccess: () => {
      queryClient.invalidateQueries([
        PeopleApi.KEY,
        'getPatientDetails',
        patientId,
      ]);
    },
  });

  if (!patientId || !patient) {
    return (
      <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
        <DoctorInnerPageLayout />
      </PrivateRoute>
    );
  }

  const handleEditClick = () => {
    navigate(generatePath(RoutePath.DoctorEditPatient, { patientId }));
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('patient_id', patientId!);

    try {
      uploadDocument(formData);
    } catch (error) {
      setUploadError('Ошибка загрузки');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const event = {
        target: {
          files: [file],
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(event);
    }
  };

  return (
    <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
      <DoctorInnerPageLayout>
        <div className="rounded-[16px] bg-brand-6">
          <header className="p-[24px]">
            <Link
              className="flex items-center gap-[6px] text-[20px] font-bold"
              to={RoutePath.DoctorPatients}
            >
              <BackIcon />
              Вернуться назад
            </Link>
          </header>
          <div className="rounded-[16px] bg-white">
            <div className="flex gap-[24px] px-[24px] py-[36px]">
              <div className="flex size-[120px] items-center justify-center rounded-[12px] bg-brand-7 text-[36px]">
                {patient.initials}
              </div>
              <div className="flex flex-col justify-between">
                <div>
                  <h1 className="mb-[6px] text-[24px]">{patient.fullName}</h1>
                  {patient.birthdate && isValid(patient.birthdate) && (
                    <span className="block">
                      {format(patient.birthdate, 'dd.MM.yyyy')}
                    </span>
                  )}
                </div>
                <div className="leading-[24px]">
                  {patient.phone && (
                    <div className="flex gap-[12px]">
                      <span className="flex-1 text-brand-1/50">Телефон</span>
                      <span>{patient.phone}</span>
                    </div>
                  )}
                  {patient.email && (
                    <div className="flex gap-[12px]">
                      <span className="flex-1 text-brand-1/50">
                        Электронная почта
                      </span>
                      <span>{patient.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end px-[24px] pb-[24px]">
              <button
                className="button button--primary px-[40px]"
                onClick={handleEditClick}
              >
                Редактировать
              </button>
            </div>
            <div className="rounded-[16px] bg-brand-6">
              <header className="flex gap-[36px] px-[24px]">
                <button
                  className={classNames(
                    styles.tab,
                    tab === 'history' && styles.active,
                  )}
                  onClick={() => setTab('history')}
                  type="button"
                >
                  История заказов
                </button>
                {patient.active_order && (
                  <button
                    className={classNames(
                      styles.tab,
                      tab === 'active' && styles.active,
                    )}
                    onClick={() => setTab('active')}
                    type="button"
                  >
                    Действующий заказ
                  </button>
                )}
                <button
                  className={classNames(
                    styles.tab,
                    tab === 'documents' && styles.active,
                  )}
                  onClick={() => setTab('documents')}
                  type="button"
                >
                  Документы
                </button>
              </header>
              {tab === 'history' && (
                <div className="rounded-[16px] bg-white px-[24px] pb-[12px] pt-[24px]">
                  <table className="table w-full">
                    <thead>
                      <tr>
                        <th className="px-[40px] py-[30px] text-left">
                          Номер заказа
                        </th>
                        <th className="px-[40px] py-[30px] text-left">
                          Дата заказа
                        </th>
                        <th className="px-[40px] py-[30px] text-left">
                          Лекарства
                        </th>
                        <th className="px-[40px] py-[30px] text-left">Цена</th>
                        <th className="px-[40px] py-[30px] text-left">
                          Статус
                        </th>
                        <th className="px-[40px] py-[30px] text-left">
                          &nbsp;
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrderHistory.map((order) => (
                        <tr key={order.id}>
                          <td className="px-[40px] py-[30px] text-left">
                            №{order.id}
                          </td>
                          <td className="px-[40px] py-[30px] text-left">
                            {order.status_date && isValid(order.status_date)
                              ? format(order.status_date, 'dd.MM.yyyy')
                              : '—'}
                          </td>
                          <td className="px-[40px] py-[30px] text-left">
                            {order.medicine_count}
                          </td>
                          <td className="px-[40px] py-[30px] text-left">
                            {formatMoney(order.total_price ?? order.price)}
                          </td>
                          <td className="px-[40px] py-[30px] text-left">
                            {(() => {
                              const cfg = getOrderStatusConfig(
                                order.status?.value ?? '',
                              );
                              return (
                                <span
                                  className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
                                >
                                  {cfg.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-[40px] py-[30px] text-left">
                            <Link
                              to={generatePath(
                                RoutePath.DoctorOrderHistoryItem,
                                { orderId: String(order.id) },
                              )}
                              className="text-brand-5 underline hover:no-underline"
                            >
                              Детали
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {tab === 'active' && (
                <>
                  {patient.active_order ? (
                    <DoctorOrderDetails orderId={patient.active_order.id} />
                  ) : (
                    <div className="rounded-[16px] bg-white p-[24px] text-center">
                      <div className="text-brand-1/50">
                        У пациента нет действующего заказа
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {tab === 'documents' && (
              <div className="rounded-[16px] bg-white px-[24px] pb-[12px] pt-[24px]">
                <table className="table w-full">
                  <tbody>
                    {patient.documents && patient.documents.length > 0 ? (
                      patient.documents.map((doc) => {
                        const fileName = doc.title;
                        const fileNameWithoutExtension = fileName
                          .split('.')
                          .slice(0, -1)
                          .join('.');

                        return (
                          <tr key={doc.id}>
                            <td className="flex !h-[68px] items-center gap-[40px]">
                              <a
                                href={`https://my-pax.ru${doc.document}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer"
                              >
                                <img
                                  src={PdfIcon}
                                  alt="PDF icon"
                                  width={30}
                                  height={30}
                                />
                              </a>
                              <div>
                                <span className="block font-medium">
                                  {fileNameWithoutExtension}
                                </span>
                                <span className="block text-[14px] leading-[20px] text-brand-1/30">
                                  {fileName}
                                </span>
                              </div>
                            </td>
                            <td className="!h-[68px]">
                              <button
                                onClick={() => deleteDocument(doc.id)}
                                className="inline-flex size-[32px] items-center justify-center rounded-[12px] bg-brand-3"
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={2}>Нет доступных документов</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="mt-[40px]">
                  <div
                    className="border-gray-400 cursor-pointer rounded-[16px] border-2 border-dashed p-[40px] text-center transition-colors hover:border-brand-3"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileDrop}
                  >
                    <p>
                      <strong className="block text-[16px] font-bold">
                        Перетащите файлы, чтобы начать загрузку
                      </strong>
                      <span className="block text-[14px] leading-[20px] text-brand-1/30">
                        Максимальный размер файла - 5 Мб
                      </span>
                    </p>
                    <span
                      className="mt-[20px] block cursor-pointer text-center text-brand-5 underline"
                      onClick={handleUploadClick}
                    >
                      Загрузить
                    </span>
                  </div>

                  <input
                    type="file"
                    accept="application/pdf"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
